/**
 * PHP legacy datetimes are China wall-clock strings; the API often serializes them
 * with a trailing Z (treated as UTC). Epoch-second strings also appear (Node mirror).
 */

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Parse PHP create_time: epoch seconds, YYYY-MM-DD HH:mm:ss, or ISO. */
export function parseLegacyPhpTime(value: string | null | undefined): Date | null {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;

  if (/^\d{9,12}$/.test(s)) {
    const d = new Date(Number(s) * 1000);
    return Number.isFinite(d.getTime()) ? d : null;
  }

  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?/.test(s) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!m) return null;
    const d = new Date(
      Date.UTC(
        Number(m[1]),
        Number(m[2]) - 1,
        Number(m[3]),
        Number(m[4]),
        Number(m[5]),
        Number(m[6] ?? '0'),
      ),
    );
    return Number.isFinite(d.getTime()) ? d : null;
  }

  const d = new Date(s);
  return Number.isFinite(d.getTime()) ? d : null;
}

/** YYYY-MM-DD for hall cards and list rows. */
export function formatLegacyPhpDate(value: string): string {
  const s = value.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const date = parseLegacyPhpTime(s);
  if (!date) return s.slice(0, 10);
  // Epoch → real UTC instant → show Asia/Shanghai calendar day.
  if (/^\d{9,12}$/.test(s)) {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
}

/** YYYY-MM-DD HH:mm:ss for detail views. */
export function formatLegacyPhpDateTime(value: string): string {
  const s = value.trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s;
  const date = parseLegacyPhpTime(s);
  if (!date) return s.replace('T', ' ').slice(0, 19);
  if (/^\d{9,12}$/.test(s)) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '00';
    return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`;
  }
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())} ${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:${pad2(date.getUTCSeconds())}`;
}

/**
 * Human registration time for sub-account cards.
 * zh: 2026年9月2日 下午12:59
 * en: Sep 2, 2026, 12:59 PM
 *
 * Epoch seconds are real UTC instants → Asia/Shanghai.
 * Wall-clock PHP strings are China local stored without TZ → format UTC parts as-is.
 */
export function formatSubAccountRegisteredAt(
  value: string | null | undefined,
  locale: string = 'zh',
): string {
  const raw = value?.trim() ?? '';
  const date = parseLegacyPhpTime(raw);
  if (!date) return raw || '—';
  const lang = locale.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US';
  const isEpoch = /^\d{9,12}$/.test(raw);
  return new Intl.DateTimeFormat(lang, {
    timeZone: isEpoch ? 'Asia/Shanghai' : 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}
